import assign from 'volta/helpers/assign';
import classNames from 'volta/helpers/class-names';
import BvColumns from 'volta/components/bv-columns';
import BvStack from 'volta/components/bv-stack';
import BvCalendarDays0 from './days';
import BvCalendarNav from './nav';
import BvCalendarRange from './range';
import { concat, hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
import PowerCalendarRange from 'ember-power-calendar/components/power-calendar-range';
import moment from 'moment-timezone';

import type BvCalendarDays from './days';
import type BvCalendarNavComponent from './nav';
import type { PowerCalendarAPI } from 'ember-power-calendar/components/power-calendar';
import type { PowerCalendarDay, SelectedPowerCalendarRange } from 'ember-power-calendar/utils';
import type { TDate } from 'volta/models/types/calendar';
import type { TSize } from 'volta/utils/style-utils';

export interface BvCalendarSignature {
  Args: {
    selected?: TDate | SelectedPowerCalendarRange;
    center?: TDate;
    withRange?: boolean;
    size?: Extract<TSize, 'sm' | 'md' | 'lg'>;
    minDate?: TDate;
    maxDate?: TDate;
    fullWidth?: boolean;
    minRange?: number;
    showNextMonth?: boolean;
    onCenterChange?: (center: PowerCalendarDay) => void;
    onSelect?: (
      day: PowerCalendarDay,
      calendar: PowerCalendarAPI,
      e: InputEvent,
      afterSelect?: (e: InputEvent) => void
    ) => void;
    afterSelect?: (e: InputEvent) => void;
  };
  Blocks: {
    default: [
      PowerCalendarAPI & {
        minDate?: Date;
        maxDate?: Date;
        Days: typeof BvCalendarDays;
        Nav: typeof BvCalendarNavComponent;
      }
    ];
  };
  Element: HTMLElement;
}

export default class BvCalendarComponent extends Component<BvCalendarSignature> {
  <template>
    {{#let
      (if @withRange (component PowerCalendarRange) (component PowerCalendar))
      (classNames
        'bv-calendar'
        (if @size (concat 'ember-power-calendar--' @size))
        (if @fullWidth 'ember-power-calendar--full-width')
      )
      (if @withRange (component BvCalendarRange) (component BvCalendarDays0))
      as |Calendar classNames daysComponent|
    }}
      {{! @glint-expect-error }}
      <Calendar
        @selected={{this.selected}}
        @navComponent={{component BvCalendarNav}}
        @daysComponent={{daysComponent}}
        @center={{this.center}}
        @onCenterChange={{optional @onCenterChange}}
        @onSelect={{this.handleSelect}}
        @minRange={{@minRange}}
        class={{classNames}}
        ...attributes
        as |calendar|
      >
        {{#let (assign calendar (hash minDate=@minDate maxDate=@maxDate)) as |api|}}
          {{#if (has-block)}}
            {{yield api}}
          {{else if @showNextMonth}}
            <BvStack @gap='2'>
              <calendar.Nav @minDate={{@minDate}} @maxDate={{@maxDate}} @showNextMonth={{true}} />
              <BvColumns @gap='1' class='u-full-width' @columns='2'>
                <calendar.Days
                  @minDate={{@minDate}}
                  @maxDate={{@maxDate}}
                  class='u-full-width'
                  @showDaysAround={{false}}
                />
                <calendar.Days
                  @center={{this.nextMonthsCenter}}
                  @minDate={{@minDate}}
                  @maxDate={{@maxDate}}
                  @showDaysAround={{false}}
                  class='u-full-width'
                />
              </BvColumns>
            </BvStack>
          {{else}}
            <calendar.Nav @minDate={{@minDate}} @maxDate={{@maxDate}} />
            <calendar.Days @minDate={{@minDate}} @maxDate={{@maxDate}} />
          {{/if}}
        {{/let}}
      </Calendar>
    {{/let}}
  </template>
  get center(): Date {
    return moment.utc(this.args.center).toDate();
  }

  get selected(): TDate | SelectedPowerCalendarRange | undefined {
    const { selected, withRange } = this.args;
    if (!selected) {
      return undefined;
    }
    if (withRange) {
      return selected;
    }

    return moment.utc(selected as Date).toDate();
  }

  get nextMonthsCenter() {
    return moment.utc(this.center).add(1, 'month');
  }

  @action
  handleSelect(day: PowerCalendarDay, calendar: any, e: InputEvent) {
    this.args.onSelect?.(day, calendar, e, this.args.afterSelect);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvCalendar: typeof BvCalendarComponent;
    'bv-calendar': typeof BvCalendarComponent;
  }
}
